import React from "react";
import { Link } from "react-router-dom";
import { Col, Form, Row, Table } from "react-bootstrap";
import { Check, CheckAll, ExclamationTriangle, Pen, X } from "react-bootstrap-icons";
import { Article } from "../../models/responses/article";
import { ArticleType } from "../../models/enums/articleTypeEnum";
import { ClientType } from "../../models/enums/clientTypeEnum";
import { Supplier } from "../../models/responses/supplier";
import { OrderPageType } from "../../models/responses/orderPageType";
import { ArticleTemplate } from "../../models/responses/articleTemplate";

// ============================================
// INTERFACES
// ============================================

export interface EditableProductTableHandlers {
  onArticleClick: (article: Article) => void;
  onArticleNumberChange: (rowNumber: number, value: string) => void;
  onArticleTemplateChange: (rowNumber: number, templateId: string) => void;
  onCommentChange: (rowNumber: number, value: string) => void;
  onDescriptionChange: (rowNumber: number, value: string) => void;
  onDiscountChange: (rowNumber: number, value: string) => void;
  onPurchaseOrderNumberChange: (rowNumber: number, value: string) => void;
  onPurchasePriceChange: (rowNumber: number, value: string) => void;
  onQuantityChange: (rowNumber: number, value: number) => void;
  onSalesPriceChange: (rowNumber: number, value: string) => void;
  onSupplierChange: (rowNumber: number, supplierId: string) => void;
  onToggleAllVerified: () => void;
  onValidatedChange: (rowNumber: number) => void;
}

interface EditableProductTableProps {
  articles: Article[];
  allProductVerified: boolean;
  clientType: ClientType;
  orderPageType: OrderPageType | null;
  suppliers: Supplier[];
  orderSupplierId: string | null;
  articleTemplates: ArticleTemplate[];
  handlers: EditableProductTableHandlers;
}

// ============================================
// COMPONENT
// ============================================

const EditableProductTable: React.FC<EditableProductTableProps> = ({
  articles,
  allProductVerified,
  clientType,
  orderPageType,
  suppliers,
  orderSupplierId,
  articleTemplates,
  handlers,
}) => {
  const productArticles = articles.filter((a) => a.articleType === ArticleType.Product);
  const isStandard = clientType === ClientType.Standard;
  const isCatalog = clientType === ClientType.Catalog;
  const isFleet = clientType === ClientType.Fleet;

  return (
    <Row>
      <h3>Artiklar</h3>
      <Col xs={12} md={12}>
        <Table responsive striped bordered hover>
          <thead>
            <tr key="-1">
              <th
                style={{ cursor: "pointer" }}
                onClick={handlers.onToggleAllVerified}
              >
                {allProductVerified ? (
                  <CheckAll className="text-success" />
                ) : (
                  <ExclamationTriangle className="text-danger" />
                )}
              </th>
              {isFleet && <th>Välj mall</th>}
              {(!orderPageType || orderPageType.articleTableArticleNumber) && <th>Art.nr</th>}
              {(!orderPageType || orderPageType.articleTableDescription) && <th>Beskrivning</th>}
              {(!orderPageType || orderPageType.articleTableQuantity) && <th>Antal</th>}
              {isFleet && <th>Saldo</th>}
              {(!orderPageType || orderPageType.articleTableSalesPrice) && <th>Försäljningspris</th>}
              {(!orderPageType || orderPageType.articleTableDiscount) && (isStandard || isFleet) && (
                <th>Rabatt (%)</th>
              )}
              {orderPageType?.articleTablePurchasePrice && <th>Inköpspris</th>}
              {(isCatalog || orderPageType?.articleTableSupplierName) && <th>Leverantör</th>}
              {orderPageType?.articleTableIONumber && <th>IO-nummer</th>}
              {orderPageType?.articleTableComment && <th>Extern kommentar</th>}
              {orderPageType?.articleTablePartUrl && isStandard && <th>PL</th>}
              {isFleet && <th>Exkluderad från order</th>}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {productArticles.map((article) => (
              <tr key={article.rowNumber}>
                {/* Verified status */}
                <td>
                  {!article.verified ? (
                    <X
                      className="text-danger"
                      onClick={() => handlers.onValidatedChange(article.rowNumber)}
                    />
                  ) : (
                    <Check
                      className="text-success"
                      onClick={() => handlers.onValidatedChange(article.rowNumber)}
                    />
                  )}
                </td>

                {/* Template selector (Fleet only) */}
                {isFleet && (
                  <td>
                    <Form.Select
                      size="sm"
                      className="mt-1"
                      value={article.articleTemplateId || ""}
                      disabled={article.verified}
                      onChange={(e) => handlers.onArticleTemplateChange(article.rowNumber, e.target.value)}
                    >
                      <option value="">- Välj mall -</option>
                      {articleTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.articleNumber} - {template.description}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                )}

                {/* Article number */}
                {(!orderPageType || orderPageType.articleTableArticleNumber) && (
                  <td>
                    <Form.Control
                      type="text"
                      value={article.articleNumber}
                      size="sm"
                      onChange={(e) => handlers.onArticleNumberChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Description */}
                {(!orderPageType || orderPageType.articleTableDescription) && (
                  <td>
                    <Form.Control
                      type="text"
                      value={article.description}
                      size="sm"
                      onChange={(e) => handlers.onDescriptionChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Quantity */}
                {(!orderPageType || orderPageType.articleTableQuantity) && (
                  <td>
                    <Form.Control
                      type="number"
                      value={article.quantity}
                      size="sm"
                      onChange={(e) => handlers.onQuantityChange(article.rowNumber, parseInt(e.target.value))}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Stock (Fleet only) */}
                {isFleet && <td align="left">{article.stock ?? "-"}</td>}

                {/* Sales price */}
                {(!orderPageType || orderPageType.articleTableSalesPrice) && (
                  <td>
                    <Form.Control
                      type="text"
                      defaultValue={article.salesPrice ?? 0}
                      value={article.salesPrice}
                      size="sm"
                      onChange={(e) => handlers.onSalesPriceChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified || article.isSyncedWithAX}
                      disabled={article.verified || article.isSyncedWithAX}
                    />
                  </td>
                )}

                {/* Discount */}
                {(!orderPageType || orderPageType.articleTableDiscount) && (isStandard || isFleet) && (
                  <td>
                    <Form.Control
                      type="text"
                      defaultValue={article.discount ?? 0}
                      value={article.discount ?? ""}
                      size="sm"
                      onChange={(e) => handlers.onDiscountChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Purchase price */}
                {orderPageType?.articleTablePurchasePrice && (
                  <td>
                    <Row>
                      <Col xs={7} className="pe-0">
                        <Form.Control
                          type="text"
                          defaultValue={article.purchasePrice ?? 0}
                          value={article.purchasePrice}
                          size="sm"
                          onChange={(e) => handlers.onPurchasePriceChange(article.rowNumber, e.target.value)}
                          readOnly={article.verified || !!article.articleTemplateId}
                          disabled={article.verified || !!article.articleTemplateId}
                        />
                      </Col>
                      <Col xs={5} className="pt-1">
                        <Form.Text>
                          {article.supplierId
                            ? suppliers.find((s) => s.id === article.supplierId)?.currency
                            : suppliers.find((s) => s.id === orderSupplierId)?.currency}
                        </Form.Text>
                      </Col>
                    </Row>
                  </td>
                )}

                {/* Supplier */}
                {(isCatalog || orderPageType?.articleTableSupplierName) && (
                  <td>
                    <Form.Select
                      aria-label="Leverantör"
                      value={article.supplierId || ""}
                      size="sm"
                      onChange={(e) => handlers.onSupplierChange(article.rowNumber, e.target.value)}
                      disabled={article.verified || !!article.articleTemplateId}
                    >
                      <option key={0} value="">
                        - Ingen leverantör vald -
                      </option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id} value={supplier.id}>
                          {supplier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                )}

                {/* IO-nummer */}
                {orderPageType?.articleTableIONumber && (
                  <td>
                    <Form.Control
                      type="text"
                      value={article.purchaseOrderNumber}
                      size="sm"
                      onChange={(e) => handlers.onPurchaseOrderNumberChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Comment */}
                {orderPageType?.articleTableComment && (
                  <td>
                    <Form.Control
                      type="text"
                      value={article.comment}
                      size="sm"
                      onChange={(e) => handlers.onCommentChange(article.rowNumber, e.target.value)}
                      readOnly={article.verified}
                      disabled={article.verified}
                    />
                  </td>
                )}

                {/* Part URL */}
                {orderPageType?.articleTablePartUrl && isStandard && (
                  <td>
                    {!article.partUrl ? (
                      <span>-</span>
                    ) : (
                      <Link
                        onClick={(e) => e.stopPropagation()}
                        target="_blank"
                        to={article.partUrl ?? ""}
                      >
                        <Check className="text-success" />
                      </Link>
                    )}
                  </td>
                )}

                {/* Exclude from warehouse (Fleet only) */}
                {isFleet && (
                  <td align="center">
                    {article.excludeFromWarehousePicking ? (
                      <span className="text-danger">Beställ ej</span>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                )}

                {/* Edit button */}
                <td onClick={() => handlers.onArticleClick(article)} role="button">
                  <Pen />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Col>
    </Row>
  );
};

export default EditableProductTable;
